/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    BooleanChip,
    CardSection,
    CustomerServiceWidget,
    DataIndicatorWidget,
    CardHeader,
    HorizontalDivider,
    VerticalDivider,
    ActionButton,
    DataIndicatorWidgetProps,
    BooleanChipProps,
    CustomerServiceWidgetProps,
} from '@europrocurement/flexy-components';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { TiersTiersPrescripteurReadCommerciaux } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    type Prescripteur,
    useSociete,
    useTiersService,
    type Utilisateur,
} from '@europrocurement/l2d-domain';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useParams } from 'react-router';
import { formatPrice } from '@europrocurement/l2d-utils';
import { enqueueSnackbar } from 'notistack';
import { AxiosResponse } from 'axios';
import { PrescriberOfferChip } from '@europrocurement/l2d-modules/modules/Offres/components/PrescriberOfferChip';
import { minusIcon, plusIcon } from '@europrocurement/l2d-icons';
import { useUserContactCard } from '@europrocurement/l2d-modules';
import { useUtilisateur } from '../../../../../hooks';
import { getSanitizedId } from '../../../../../utils/sanitize';
import { PrescriberTabProps } from './types';
import { ActivityHistorySection } from './ResumeSections/ActivityHistorySection';

const MIN_CUSTOMER_SERVICE_ITEMS_DISPLAYED = 4;

type LoadedValue<T> = {
    value: T | null;
    loaded: boolean;
};

type ResumeDataState = {
    hasB2BAccess: LoadedValue<boolean>;
    outstandingAmount: LoadedValue<string>;
    annualRevenue: LoadedValue<string>;
    customerServiceUsers: LoadedValue<CustomerServiceWidgetProps[]>;
};

interface UserInfoCache {
    [email: string]: Utilisateur | null;
}

const Resume: React.FunctionComponent<PrescriberTabProps> = function ({
    prescriber,
    fetchPrescriber,
}) {
    const theme = useTheme();
    const [isCustomerServiceListExpanded, setIsCustomerServiceListExpanded] = useState(false);
    const [userCache, setUserCache] = useState<UserInfoCache>({});

    const xIdSociete = useSociete();
    const { prescribersApi } = useTiersService();
    const { getUserByEmail } = useUtilisateur();
    const { request } = useApiRequest();
    const { idPrescripteur: prescriberId } = useParams();
    const { callUserContactCardModal } = useUserContactCard();

    const defaultLoadedValue = useMemo(
        () => ({
            value: null,
            loaded: false,
        }),
        [],
    );

    const { relationsSocietes, formaliste } = prescriber;
    const companyRelationship = relationsSocietes[0] ?? null;
    const hasB2BAccess = companyRelationship?.plateformeB2BAutorise ?? false;
    const recoveryResponsible = companyRelationship?.chargeDeRecouvrement;

    const [resumeData, setResumeData] = useState<ResumeDataState>(() => ({
        outstandingAmount: defaultLoadedValue,
        annualRevenue: defaultLoadedValue,
        hasB2BAccess: {
            value: hasB2BAccess,
            loaded: true,
        },
        customerServiceUsers: {
            value: [],
            loaded: false,
        },
    }));

    const { breakpoints } = theme;
    const isLargeScreen = useMediaQuery(breakpoints.down('lg'));
    const isMediumScreen = useMediaQuery(breakpoints.down('md'));

    const resolveUserInfo = useCallback(
        async (email: string): Promise<Utilisateur | null> => {
            if (userCache[email] !== undefined) {
                return userCache[email];
            }

            try {
                const user = await getUserByEmail(email);
                setUserCache((prev) => ({ ...prev, [email]: user }));
                return user;
            } catch (error) {
                console.warn(`Impossible de récupérer l'utilisateur avec l'email: ${email}`, error);
                setUserCache((prev) => ({ ...prev, [email]: null }));
                return null;
            }
        },
        [getUserByEmail, userCache],
    );

    const updateResumeData = useCallback(
        (key: keyof ResumeDataState, value: ResumeDataState[keyof ResumeDataState]) => {
            setResumeData((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        },
        [],
    );

    const toggleB2BAccess = useCallback(async () => {
        const newAccessValue = !resumeData.hasB2BAccess.value;
        const updatePayload = { accesPlateformeB2B: newAccessValue };
        let isUpdateSuccessful = false;

        updateResumeData('hasB2BAccess', {
            value: newAccessValue,
            loaded: false,
        });

        try {
            await prescribersApi.updateDroitsPrescripteurTiersItem({
                id: `${prescriber.id}`,
                updateDroitsPrescripteurTiersItemRequest: updatePayload,
                xIdSociete,
            });
            isUpdateSuccessful = true;
        } catch {
            enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
        }

        updateResumeData('hasB2BAccess', {
            value: isUpdateSuccessful ? newAccessValue : resumeData.hasB2BAccess.value,
            loaded: true,
        });

        if (isUpdateSuccessful) {
            fetchPrescriber();
        }
    }, [
        resumeData.hasB2BAccess.value,
        prescribersApi,
        prescriber.id,
        xIdSociete,
        updateResumeData,
        fetchPrescriber,
    ]);

    const fetchOutstandingAmount = useCallback(
        () =>
            prescribersApi.getPrescriberOutstandingTiersItem({
                id: prescriberId ?? '',
                xIdSociete,
            }),
        [prescribersApi, prescriberId, xIdSociete],
    );

    const fetchAnnualRevenue = useCallback(
        () =>
            prescribersApi.getPrescriberAnnualSalesTiersItem({
                id: prescriberId ?? '',
                xIdSociete,
            }),
        [prescribersApi, prescriberId, xIdSociete],
    );

    const fetchCommercialUsers = useCallback(
        () =>
            prescribersApi.getCommerciauxPrescripteurTiersItem({
                id: prescriberId ?? '',
                xIdSociete,
            }),
        [prescribersApi, prescriberId, xIdSociete],
    );

    const handleOutstandingAmountSuccess = useCallback(
        (response: AxiosResponse) => {
            const amount = response?.data?.encours;
            updateResumeData('outstandingAmount', {
                value: amount ? `${formatPrice(amount)} €` : '-',
                loaded: true,
            });
        },
        [updateResumeData],
    );

    const handleAnnualRevenueSuccess = useCallback(
        (response: AxiosResponse) => {
            const revenue = response?.data?.chiffre_affaire_annuel_HT;
            updateResumeData('annualRevenue', {
                value: revenue ? `${formatPrice(revenue)} €` : '-',
                loaded: true,
            });
        },
        [updateResumeData],
    );

    const getPrescriberDomainStatus = useCallback(
        (domainId?: number) => {
            if (!domainId) {
                return null;
            }
            const prescriberDomain = prescriber.domaines?.find(
                (domain: any) => domain.id === domainId,
            );
            const domainStatus = prescriberDomain?.statut;
            return domainStatus
                ? {
                      id: domainStatus.id,
                      label: domainStatus.libelle,
                  }
                : null;
        },
        [prescriber.domaines],
    );

    const handleCommercialUsersSuccess = useCallback(
        (response: AxiosResponse) => {
            const prescriberCompany = prescriber?.relationsSocietes[0]; // Todo define default company / behaviour in case of multiples companies
            const billingCode = prescriberCompany?.codeClient ?? '';

            const commercialUsers: TiersTiersPrescripteurReadCommerciaux['commerciaux'] =
                response?.data?.commerciaux;
            if (commercialUsers) {
                const formattedUsers = commercialUsers.map((user): CustomerServiceWidgetProps => {
                    const domainStatus = getPrescriberDomainStatus(user.domaine?.id);
                    return {
                        firstName: user.utilisateur?.prenom ?? '',
                        lastName: user.utilisateur?.nom ?? '',
                        userIdentifier: user.utilisateur?.userIdentifier ?? '',
                        userId: user.utilisateur?.id,
                        sector: user.domaine?.libelle ?? '',
                        status: domainStatus ?? undefined,
                        onClick: () => {
                            if (user.utilisateur?.id) {
                                callUserContactCardModal({
                                    userId: user.utilisateur?.id ?? '',
                                    userProps: user.utilisateur,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source: 'BACK',
                                    userType: 'COMMERCIAL',
                                    mailSubject: `Information Prescripteur ${prescriber.raisonSociale} ${billingCode}`,
                                    tiersIds: [prescriber.id as number],
                                });
                            }
                        },
                    };
                });

                if (formaliste) {
                    const formalistUser: CustomerServiceWidgetProps = {
                        firstName: formaliste.prenom ?? '',
                        lastName: formaliste.nom ?? '',
                        userIdentifier: formaliste.userIdentifier ?? '',
                        sector: 'Formaliste',
                        onClick: () => {
                            if (formaliste.id) {
                                callUserContactCardModal({
                                    userId: formaliste.id ?? '',
                                    userProps: formaliste,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source: 'BACK',
                                    userType: 'COMMERCIAL',
                                    mailSubject: `Information Prescripteur ${prescriber.raisonSociale} ${billingCode}`,
                                    tiersIds: [prescriber.id as number],
                                });
                            }
                        },
                    };
                    formattedUsers.splice(2, 0, formalistUser);
                }

                if (recoveryResponsible) {
                    const recoveryResponsibleUser: CustomerServiceWidgetProps = {
                        firstName: recoveryResponsible.prenom ?? '',
                        lastName: recoveryResponsible.nom ?? '',
                        userIdentifier: recoveryResponsible.userIdentifier ?? '',
                        sector: 'Chargé de recouvrement',
                        onClick: () => {
                            if (recoveryResponsible.id) {
                                callUserContactCardModal({
                                    userId: recoveryResponsible.id ?? '',
                                    userProps: recoveryResponsible,
                                    displayActionContact: true,
                                    displayUserSchedule: true,
                                    source: 'BACK',
                                    userType: 'COMMERCIAL',
                                    mailSubject: `Information Prescripteur ${prescriber.raisonSociale} ${billingCode}`,
                                    tiersIds: [prescriber.id as number],
                                });
                            }
                        },
                    };
                    formattedUsers.splice(3, 0, recoveryResponsibleUser);
                }

                updateResumeData('customerServiceUsers', {
                    value: formattedUsers,
                    loaded: true,
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getPrescriberDomainStatus, formaliste, recoveryResponsible, updateResumeData],
    );

    const apiCallsConfiguration = useMemo(
        () => [
            {
                apiCall: fetchOutstandingAmount,
                successHandler: handleOutstandingAmountSuccess,
            },
            {
                apiCall: fetchAnnualRevenue,
                successHandler: handleAnnualRevenueSuccess,
            },
            {
                apiCall: fetchCommercialUsers,
                successHandler: handleCommercialUsersSuccess,
            },
        ],
        [
            fetchOutstandingAmount,
            handleOutstandingAmountSuccess,
            fetchAnnualRevenue,
            handleAnnualRevenueSuccess,
            fetchCommercialUsers,
            handleCommercialUsersSuccess,
        ],
    );

    useEffect(() => {
        if (getSanitizedId(prescriberId)) {
            apiCallsConfiguration.forEach(({ apiCall, successHandler }) => {
                request(apiCall(), {
                    successCallback: successHandler,
                    withToaster: false,
                });
            });
        }
    }, [prescriberId, apiCallsConfiguration, request]);

    const toggleCustomerServiceList = useCallback(() => {
        setIsCustomerServiceListExpanded(!isCustomerServiceListExpanded);
    }, [isCustomerServiceListExpanded]);

    const shouldShowVerticalDivider = useCallback(
        (index: number): boolean => {
            if (index < 1 || isMediumScreen) {
                return false;
            }
            if (isLargeScreen) {
                return index % 2 !== 0;
            }
            return index % 4 !== 0;
        },
        [isMediumScreen, isLargeScreen],
    );

    const getFlexItemSize = useCallback(() => {
        if (isMediumScreen) {
            return '100%';
        }
        if (isLargeScreen) {
            return '50%';
        }
        return '25%';
    }, [isMediumScreen, isLargeScreen]);

    const booleanChipsContainerStyle = useMemo(
        () => ({ display: 'flex', margin: '15px 32px 0px' }),
        [],
    );
    const flexContainerStyle = useMemo(() => ({ display: 'flex', flexWrap: 'wrap' }), []);
    const flexItemSize = getFlexItemSize();
    const flexItemStyle = useMemo(
        () => ({
            display: 'flex',
            flex: flexItemSize,
            minWidth: flexItemSize,
            maxWidth: flexItemSize,
            marginY: '8px',
        }),
        [flexItemSize],
    );

    const booleanChipsConfig = useMemo(
        (): Array<BooleanChipProps & { key: string }> => [
            {
                label: 'Accès B2B',
                icon: ['fasl', 'suitcase'] as any,
                isChecked: !!resumeData.hasB2BAccess.value,
                isLoading: !resumeData.hasB2BAccess.loaded,
                key: 'prescriber_b2b_access_chip',
                onClick: toggleB2BAccess,
            },
        ],
        [resumeData.hasB2BAccess.value, resumeData.hasB2BAccess.loaded, toggleB2BAccess],
    );

    const dataIndicators = useMemo(
        (): Array<DataIndicatorWidgetProps & { key: string }> => [
            {
                label: "Chiffres d'affaires annuel",
                value: resumeData.annualRevenue.value,
                key: 'prescriber_annual_revenue_indicator',
                loading: !resumeData.annualRevenue.loaded,
            },
            {
                label: 'Encours',
                value: resumeData.outstandingAmount.value,
                key: 'prescriber_outstanding_indicator',
                loading: !resumeData.outstandingAmount.loaded,
            },
        ],
        [
            resumeData.annualRevenue.value,
            resumeData.annualRevenue.loaded,
            resumeData.outstandingAmount.value,
            resumeData.outstandingAmount.loaded,
        ],
    );

    const customerServiceUsers = resumeData.customerServiceUsers.value;
    const listToggleIcon = isCustomerServiceListExpanded ? minusIcon : plusIcon;
    const listToggleLabel = `Voir ${isCustomerServiceListExpanded ? 'moins' : 'plus'}`;

    const cardHeaderContent = useMemo(
        () => (
            <Box>
                <CardHeader
                    title="Indicateurs"
                    border={false}
                />
                <Box sx={booleanChipsContainerStyle}>
                    {booleanChipsConfig.map((chipConfig) => {
                        const { key, ...otherProps } = chipConfig;
                        return (
                            <BooleanChip
                                key={key}
                                {...otherProps}
                            />
                        );
                    })}
                    <PrescriberOfferChip tiersId={prescriber.id as number} />
                </Box>
            </Box>
        ),
        [booleanChipsContainerStyle, booleanChipsConfig, prescriber.id],
    );

    return (
        <>
            <CardSection
                header={cardHeaderContent}
                sx={{ marginTop: '0px' }}
            >
                <Box sx={flexContainerStyle}>
                    {dataIndicators.map(({ key, ...indicatorProps }) => (
                        <Box
                            key={key}
                            sx={flexItemStyle}
                        >
                            <DataIndicatorWidget {...indicatorProps} />
                        </Box>
                    ))}
                </Box>
                <HorizontalDivider sx={{ margin: '30px 0px 20px' }} />
                <Box sx={flexContainerStyle}>
                    {customerServiceUsers &&
                        customerServiceUsers.map((user, index) => {
                            if (
                                index + 1 > MIN_CUSTOMER_SERVICE_ITEMS_DISPLAYED &&
                                !isCustomerServiceListExpanded
                            ) {
                                return null;
                            }
                            const userKey = `prescriber_customer_service_user_${index}`;
                            return (
                                <Box
                                    key={userKey}
                                    sx={flexItemStyle}
                                >
                                    {shouldShowVerticalDivider(index) && (
                                        <VerticalDivider sx={{ marginRight: '25px' }} />
                                    )}
                                    <CustomerServiceWidget {...user} />
                                </Box>
                            );
                        })}
                </Box>
                {customerServiceUsers &&
                    customerServiceUsers.length > MIN_CUSTOMER_SERVICE_ITEMS_DISPLAYED && (
                        <ActionButton
                            action={toggleCustomerServiceList}
                            icon={listToggleIcon}
                            label={listToggleLabel}
                            sx={{ marginTop: '25px', paddingLeft: 0, backgroundColor: '#177BC2' }}
                        />
                    )}
            </CardSection>
            <ActivityHistorySection
                resolveUserInfo={resolveUserInfo}
                prescriber={prescriber as Prescripteur}
            />
        </>
    );
};

export default Resume;
